<template>
  <div class="entity-list-page rag-analytics-demo">
    <header class="list-header">
      <div>
        <router-link to="/projects" class="back-link">← Projects</router-link>
        <h1>RAG analytics</h1>
        <p class="list-sub">Demo report — compliance intelligence sample for project 325.</p>
      </div>
      <div class="header-actions">
        <router-link
          v-if="canAccessRagAdmin"
          to="/rag-admin-dashboard"
          class="btn btn-primary"
        >
          <i class="fas fa-cogs" aria-hidden="true"></i>
          RAG admin
        </router-link>
      </div>
    </header>

    <div class="metrics-bar">
          <div class="metric-card">
            <div class="metric-value">200</div>
            <div class="metric-label">Documents Analyzed</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">1,000</div>
            <div class="metric-label">AI Queries</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">$20.00</div>
            <div class="metric-label">Analysis Cost</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">71%</div>
            <div class="metric-label">Compliance Rate</div>
          </div>
    </div>

    <!-- Main Dashboard -->
    <div class="dashboard-section">
        <div class="dashboard-grid">
          
          <!-- Compliance Intelligence Panel -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-shield-alt"></i> Compliance Intelligence</h3>
            </div>
            <div class="card-content">
              <div class="compliance-metric">
                <div class="metric-row">
                  <span class="label">Primary/Noncontributory Compliance</span>
                  <div class="progress-bar">
                    <div class="progress-fill" style="width: 71%"></div>
                    <span class="percentage">71.0%</span>
                  </div>
                </div>
                <div class="metric-detail">142 of 200 documents compliant</div>
              </div>
              
              <div class="compliance-metric">
                <div class="metric-row">
                  <span class="label">CG 2010 Endorsement Coverage</span>
                  <div class="progress-bar">
                    <div class="progress-fill success" style="width: 89.5%"></div>
                    <span class="percentage">89.5%</span>
                  </div>
                </div>
                <div class="metric-detail">179 of 200 documents have endorsements</div>
              </div>
            </div>
          </div>

          <!-- Carrier Distribution Panel -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-building"></i> Carrier Analysis</h3>
            </div>
            <div class="card-content">
              <div class="carrier-chart">
                <div class="carrier-item">
                  <div class="carrier-bar">
                    <div class="bar-fill travelers" style="width: 96%"></div>
                  </div>
                  <div class="carrier-info">
                    <span class="carrier-name">Travelers/American Zurich</span>
                    <span class="carrier-percentage">96.0% (192 docs)</span>
                  </div>
                </div>
                <div class="carrier-item">
                  <div class="carrier-bar">
                    <div class="bar-fill liberty" style="width: 4%"></div>
                  </div>
                  <div class="carrier-info">
                    <span class="carrier-name">Liberty Mutual</span>
                    <span class="carrier-percentage">4.0% (8 docs)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ROI Projections Panel -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-calculator"></i> Scaling Projections</h3>
            </div>
            <div class="card-content">
              <div class="roi-metrics">
                <div class="roi-item">
                  <div class="roi-value">$0.10</div>
                  <div class="roi-label">Cost per Document</div>
                </div>
                <div class="roi-item highlight">
                  <div class="roi-value">$7,600</div>
                  <div class="roi-label">Full 76K Analysis Cost</div>
                </div>
                <div class="roi-item warning">
                  <div class="roi-value">22,040</div>
                  <div class="roi-label">Estimated Issues (76K)</div>
                </div>
                <div class="roi-item success">
                  <div class="roi-value">80%</div>
                  <div class="roi-label">Time Savings Potential</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Company Intelligence Panel -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-building"></i> Company Intelligence</h3>
            </div>
            <div class="card-content">
              <div class="companies-breakdown">
                <div class="company-section">
                  <h4><i class="fas fa-star"></i> Primary Additional Insured</h4>
                  <div class="company-card featured">
                    <div class="company-info">
                      <div class="company-name">Great Southern Homes</div>
                      <div class="company-details">90 North Royal Tower Drive, Birmingham, AL</div>
                      <div class="company-stats">
                        <span class="stat">12 Policies</span>
                        <span class="stat">100% Compliance</span>
                        <span class="stat">Primary/Noncontributory: ✓</span>
                      </div>
                    </div>
                    <div class="risk-indicator low">Low Risk</div>
                  </div>
                </div>

                <div class="company-section">
                  <h4><i class="fas fa-users"></i> Other Identified Entities</h4>
                  <div class="entity-grid">
                    <div class="entity-card">
                      <div class="entity-name">Birmingham Construction LLC</div>
                      <div class="entity-role">Certificate Holder</div>
                      <div class="entity-stats">3 docs • 67% compliant</div>
                    </div>
                    <div class="entity-card">
                      <div class="entity-name">Alabama Development Corp</div>
                      <div class="entity-role">Additional Insured</div>
                      <div class="entity-stats">2 docs • 100% compliant</div>
                    </div>
                    <div class="entity-card">
                      <div class="entity-name">Southeast Properties</div>
                      <div class="entity-role">Named Insured</div>
                      <div class="entity-stats">8 docs • 75% compliant</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Document Analysis Insights -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-file-contract"></i> Document Intelligence</h3>
            </div>
            <div class="card-content">
              <div class="document-insights">
                <div class="insight-category">
                  <h4>Policy Forms Analysis</h4>
                  <div class="forms-list">
                    <div class="form-item">
                      <span class="form-code">CG 2010 04/13</span>
                      <span class="form-desc">Additional Insured - Owners, Lessees</span>
                      <span class="form-count">156 docs</span>
                    </div>
                    <div class="form-item">
                      <span class="form-code">CG 2010 10/01</span>
                      <span class="form-desc">Additional Insured - Ongoing Operations</span>
                      <span class="form-count">23 docs</span>
                    </div>
                    <div class="form-item">
                      <span class="form-code">Liberty GL-001</span>
                      <span class="form-desc">Liberty Mutual Proprietary Form</span>
                      <span class="form-count">8 docs</span>
                    </div>
                  </div>
                </div>

                <div class="insight-category">
                  <h4>Coverage Patterns</h4>
                  <div class="coverage-grid">
                    <div class="coverage-item">
                      <div class="coverage-icon">🛡️</div>
                      <div class="coverage-text">
                        <div class="coverage-title">Blanket Additional Insured</div>
                        <div class="coverage-stat">Found in 45 documents</div>
                      </div>
                    </div>
                    <div class="coverage-item">
                      <div class="coverage-icon">⚖️</div>
                      <div class="coverage-text">
                        <div class="coverage-title">Waiver of Subrogation</div>
                        <div class="coverage-stat">Present in 180 documents</div>
                      </div>
                    </div>
                    <div class="coverage-item">
                      <div class="coverage-icon">📋</div>
                      <div class="coverage-text">
                        <div class="coverage-title">30-Day Notice</div>
                        <div class="coverage-stat">Required in 195 documents</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Risk Assessment by Entity -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-shield-virus"></i> Risk Assessment Matrix</h3>
            </div>
            <div class="card-content">
              <div class="risk-matrix">
                <div class="risk-header">
                  <div class="risk-col">Entity</div>
                  <div class="risk-col">Policies</div>
                  <div class="risk-col">Compliance</div>
                  <div class="risk-col">Risk Level</div>
                  <div class="risk-col">Action Required</div>
                </div>
                <div class="risk-row">
                  <div class="risk-cell entity">Great Southern Homes</div>
                  <div class="risk-cell">12</div>
                  <div class="risk-cell">
                    <div class="compliance-indicator excellent">100%</div>
                  </div>
                  <div class="risk-cell">
                    <div class="risk-badge low">Low</div>
                  </div>
                  <div class="risk-cell">✅ None</div>
                </div>
                <div class="risk-row">
                  <div class="risk-cell entity">Southeast Properties</div>
                  <div class="risk-cell">8</div>
                  <div class="risk-cell">
                    <div class="compliance-indicator good">75%</div>
                  </div>
                  <div class="risk-cell">
                    <div class="risk-badge medium">Medium</div>
                  </div>
                  <div class="risk-cell">⚠️ Review 2 policies</div>
                </div>
                <div class="risk-row">
                  <div class="risk-cell entity">Birmingham Construction</div>
                  <div class="risk-cell">3</div>
                  <div class="risk-cell">
                    <div class="compliance-indicator warning">67%</div>
                  </div>
                  <div class="risk-cell">
                    <div class="risk-badge medium">Medium</div>
                  </div>
                  <div class="risk-cell">🔍 Audit required</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sample Document Analysis -->
          <div class="dashboard-card full-width">
            <div class="card-header">
              <h3><i class="fas fa-search-plus"></i> Sample Document Analysis Results</h3>
            </div>
            <div class="card-content">
              <div class="sample-analyses">
                <div class="analysis-item">
                  <div class="doc-header">
                    <div class="doc-name">67100c19c286f.pdf</div>
                    <div class="doc-status success">✓ Fully Compliant</div>
                  </div>
                  <div class="analysis-results">
                    <div class="result-row">
                      <span class="query">Additional Insured:</span>
                      <span class="answer">Great Southern Homes</span>
                      <span class="confidence high">85% confidence</span>
                    </div>
                    <div class="result-row">
                      <span class="query">Certificate Holder:</span>
                      <span class="answer">Great Southern Homes, 90 North Royal Tower Drive</span>
                      <span class="confidence high">90% confidence</span>
                    </div>
                    <div class="result-row">
                      <span class="query">Carrier:</span>
                      <span class="answer">American Zurich (Travelers Group)</span>
                      <span class="confidence high">95% confidence</span>
                    </div>
                  </div>
                </div>
                
                <div class="analysis-item">
                  <div class="doc-header">
                    <div class="doc-name">670ef73da9787.pdf</div>
                    <div class="doc-status warning">⚠️ Needs Review</div>
                  </div>
                  <div class="analysis-results">
                    <div class="result-row">
                      <span class="query">Additional Insured:</span>
                      <span class="answer">Index page only - full document required</span>
                      <span class="confidence low">50% confidence</span>
                    </div>
                    <div class="result-row">
                      <span class="query">Primary/Noncontributory:</span>
                      <span class="answer">Yes - Referenced on page 5</span>
                      <span class="confidence medium">75% confidence</span>
                    </div>
                    <div class="result-row">
                      <span class="query">Carrier:</span>
                      <span class="answer">Liberty Mutual Insurance</span>
                      <span class="confidence high">99% confidence</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Business Intelligence Panel -->
          <div class="dashboard-card full-width">
            <div class="card-header">
              <h3><i class="fas fa-lightbulb"></i> Key Business Insights</h3>
            </div>
            <div class="card-content">
              <div class="insights-grid">
                <div class="insight-item">
                  <i class="fas fa-check-circle"></i>
                  <span>Successfully analyzed 200 documents with 1,000 AI queries</span>
                </div>
                <div class="insight-item">
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>29% of documents show compliance gaps in Primary/Noncontributory language</span>
                </div>
                <div class="insight-item">
                  <i class="fas fa-chart-pie"></i>
                  <span>Strong market concentration with 96% Travelers policies</span>
                </div>
                <div class="insight-item">
                  <i class="fas fa-rocket"></i>
                  <span>System ready to scale to full 76,000 document repository</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Next Steps Panel -->
          <div class="dashboard-card full-width">
            <div class="card-header">
              <h3><i class="fas fa-tasks"></i> Recommended Next Steps</h3>
            </div>
            <div class="card-content">
              <div class="steps-list">
                <div class="step-item">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <div class="step-title">Scale to Full Repository</div>
                    <div class="step-description">Deploy RAG analysis across all 76,000 documents</div>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <div class="step-title">Automated Compliance Scoring</div>
                    <div class="step-description">Implement real-time compliance monitoring system</div>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <div class="step-title">Carrier Risk Models</div>
                    <div class="step-description">Build predictive models for carrier-specific requirements</div>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">4</div>
                  <div class="step-content">
                    <div class="step-title">Integration & Automation</div>
                    <div class="step-description">Connect with existing policy management workflows</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
    </div>

    <!-- Demo Badge -->
    <div class="demo-badge">
      <i class="fas fa-flask"></i>
      DEMO MODE
    </div>
  </div>
</template>

<script>
import auth from '@/services/auth';

export default {
  name: 'RAGAnalyticsDemo',
  computed: {
    canAccessRagAdmin() {
      return auth.isAdmin();
    },
  },
  data() {
    return {
      // Static demo data based on the actual analysis results
      analysisData: {
        totalDocuments: 200,
        totalQueries: 1000,
        totalCost: 20.00,
        complianceRate: 71.0,
        endorsementCoverage: 89.5,
        carriers: [
          { name: 'Travelers/American Zurich', percentage: 96.0, count: 192 },
          { name: 'Liberty Mutual', percentage: 4.0, count: 8 }
        ]
      }
    }
  },
  mounted() {
    // Add any initialization logic here
    console.log('RAG Analytics Demo loaded');
  }
}
</script>

<style scoped>
.rag-analytics-demo {
  /* Page chrome: global .entity-list-page */
  padding-bottom: 2.5rem;
  position: relative;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.metrics-bar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.metrics-bar .metric-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem;
  text-align: left;
}

.metrics-bar .metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
}

.metrics-bar .metric-label {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.2rem;
}

.dashboard-section {
  padding: 0;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1rem;
}

.dashboard-card {
  background: white;
  border-radius: 10px;
  box-shadow: none;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: border-color 0.15s ease;
}

.dashboard-card:hover {
  border-color: #cbd5e1;
  transform: none;
  box-shadow: none;
}

.dashboard-card.full-width {
  grid-column: 1 / -1;
}

.card-header {
  background: #f8fafc;
  color: #0f172a;
  padding: 1rem 1.15rem;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h3 {
  margin: 0;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-content {
  padding: 1.25rem;
}

/* Compliance Metrics */
.compliance-metric {
  margin-bottom: 2rem;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.label {
  font-weight: 600;
  color: #2c3e50;
}

.progress-bar {
  position: relative;
  background: #ecf0f1;
  height: 25px;
  border-radius: 12px;
  overflow: hidden;
  min-width: 150px;
  display: flex;
  align-items: center;
}

.progress-fill {
  background: linear-gradient(90deg, #e74c3c, #f39c12);
  height: 100%;
  border-radius: 12px;
  transition: width 1s ease;
}

.progress-fill.success {
  background: linear-gradient(90deg, #27ae60, #2ecc71);
}

.percentage {
  position: absolute;
  right: 10px;
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
}

.metric-detail {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-left: 1rem;
}

/* Carrier Chart */
.carrier-chart {
  space-y: 1rem;
}

.carrier-item {
  margin-bottom: 1.5rem;
}

.carrier-bar {
  background: #ecf0f1;
  height: 20px;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.bar-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 1s ease;
}

.bar-fill.travelers {
  background: linear-gradient(90deg, #3498db, #2980b9);
}

.bar-fill.liberty {
  background: linear-gradient(90deg, #e67e22, #d35400);
}

.carrier-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
}

.carrier-name {
  font-weight: 600;
  color: #2c3e50;
}

.carrier-percentage {
  color: #7f8c8d;
}

/* ROI Metrics */
.roi-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.roi-item {
  text-align: center;
  padding: 1rem;
  border-radius: 8px;
  background: #f8f9fa;
  border: 2px solid transparent;
}

.roi-item.highlight {
  background: #e3f2fd;
  border-color: #2196f3;
}

.roi-item.warning {
  background: #fff3e0;
  border-color: #ff9800;
}

.roi-item.success {
  background: #e8f5e8;
  border-color: #4caf50;
}

.roi-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.roi-label {
  font-size: 0.8rem;
  color: #7f8c8d;
}

/* Entity List */
.entity-list {
  space-y: 1rem;
}

.entity-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.entity-name {
  font-weight: 600;
  color: #2c3e50;
}

.entity-type {
  color: #7f8c8d;
  font-size: 0.9rem;
}

.confidence-badge {
  background: #27ae60;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
}

/* Insights Grid */
.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.insight-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #2c3e50;
}

.insight-item i {
  color: #3498db;
  font-size: 1.2rem;
}

/* Steps List */
.steps-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.step-item {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #3498db;
}

.step-number {
  background: #3498db;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.step-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.step-description {
  color: #7f8c8d;
  font-size: 0.9rem;
}

/* Demo Badge */
.demo-badge {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
  padding: 0.4rem 0.75rem;
  border-radius: 0.45rem;
  font-weight: 650;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  z-index: 100;
  box-shadow: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }
  
  .metrics-bar {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  
  .roi-metrics {
    grid-template-columns: 1fr;
  }
  
  .steps-list {
    grid-template-columns: 1fr;
  }
}

/* Company Intelligence Styles */
.companies-breakdown {
  /* space between child elements handled by margins */
}

.company-section {
  margin-bottom: 2rem;
}

.company-section h4 {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.company-card {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid #27ae60;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.company-card.featured {
  border-left-color: #f39c12;
  background: linear-gradient(135deg, #fff3cd, #ffeaa7);
}

.company-name {
  font-size: 1.3rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.company-details {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.company-stats {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.company-stats .stat {
  background: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #2c3e50;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.risk-indicator {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9rem;
}

.risk-indicator.low {
  background: #d4edda;
  color: #155724;
}

.entity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.entity-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.entity-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.entity-card .entity-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.entity-card .entity-role {
  color: #6c757d;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}

.entity-card .entity-stats {
  color: #495057;
  font-size: 0.8rem;
}

/* Document Insights Styles */
.document-insights {
  /* space between child elements handled by margins */
}

.insight-category {
  margin-bottom: 2rem;
}

.insight-category h4 {
  color: #2c3e50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.forms-list {
  /* spacing handled by individual form-item margins */
}

.form-item {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #3498db;
}

.form-code {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  color: #2c3e50;
  background: white;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  min-width: 100px;
}

.form-desc {
  flex: 1;
  margin: 0 1rem;
  color: #495057;
}

.form-count {
  background: #3498db;
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.coverage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.coverage-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: transform 0.2s ease;
}

.coverage-item:hover {
  transform: translateX(5px);
  border-color: #3498db;
}

.coverage-icon {
  font-size: 1.5rem;
  width: 40px;
  text-align: center;
}

.coverage-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.coverage-stat {
  color: #6c757d;
  font-size: 0.9rem;
}

/* Risk Assessment Matrix Styles */
.risk-matrix {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.risk-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 2fr;
  background: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
}

.risk-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 2fr;
  border-top: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
}

.risk-row:hover {
  background: #f8f9fa;
}

.risk-col, .risk-cell {
  padding: 1rem;
  display: flex;
  align-items: center;
  border-right: 1px solid #e9ecef;
}

.risk-col:last-child, .risk-cell:last-child {
  border-right: none;
}

.risk-cell.entity {
  font-weight: 600;
  color: #2c3e50;
}

.compliance-indicator {
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.85rem;
}

.compliance-indicator.excellent {
  background: #d4edda;
  color: #155724;
}

.compliance-indicator.good {
  background: #d1ecf1;
  color: #0c5460;
}

.compliance-indicator.warning {
  background: #ffeaa7;
  color: #856404;
}

.risk-badge {
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.8rem;
}

.risk-badge.low {
  background: #d4edda;
  color: #155724;
}

.risk-badge.medium {
  background: #fff3cd;
  color: #856404;
}

.risk-badge.high {
  background: #f8d7da;
  color: #721c24;
}

/* Sample Document Analysis Styles */
.sample-analyses {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

.analysis-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.doc-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.doc-name {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #2c3e50;
}

.doc-status {
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 600;
}

.doc-status.success {
  background: #d4edda;
  color: #155724;
}

.doc-status.warning {
  background: #fff3cd;
  color: #856404;
}

.analysis-results {
  padding: 1rem;
}

.result-row {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f3f4;
  align-items: center;
}

.result-row:last-child {
  border-bottom: none;
}

.result-row .query {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.result-row .answer {
  color: #2c3e50;
  font-size: 0.9rem;
}

.confidence {
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.confidence.high {
  background: #d4edda;
  color: #155724;
}

.confidence.medium {
  background: #d1ecf1;
  color: #0c5460;
}

.confidence.low {
  background: #ffeaa7;
  color: #856404;
}

/* Additional responsive adjustments for new components */
@media (max-width: 768px) {
  .entity-grid {
    grid-template-columns: 1fr;
  }
  
  .coverage-grid {
    grid-template-columns: 1fr;
  }
  
  .company-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .company-stats {
    justify-content: flex-start;
  }
  
  .risk-header, .risk-row {
    grid-template-columns: 1fr;
  }
  
  .risk-col, .risk-cell {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    padding: 0.75rem;
  }
  
  .form-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .sample-analyses {
    grid-template-columns: 1fr;
  }
  
  .result-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    text-align: left;
  }
}
</style> 